/**
 * HelpGuidesPage — practical guidance at /help
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, LifeBuoy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { HelpGuideAccordion, HelpGuideNav, HelpQuickHelpRow } from '../components/help';
import {
  HELP_DEFAULT_OPEN_SECTIONS,
  HELP_GUIDE_SECTIONS,
  HELP_NAV_ITEMS,
  HELP_PAGE,
  HELP_QUICK_CARDS,
} from '../content/helpGuidesContent';
import { PublicFooter } from '../components/layout/PublicFooter';

function scrollToSection(sectionId) {
  if (!sectionId) return;
  const el = document.getElementById(sectionId);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function getHashSection() {
  const hash = window.location.hash?.replace('#', '');
  if (!hash) return null;
  return HELP_NAV_ITEMS.some((item) => item.id === hash) ? hash : null;
}

function getInitialOpenSections() {
  const hash = getHashSection();
  if (hash) {
    return [...new Set([...HELP_DEFAULT_OPEN_SECTIONS, hash])];
  }
  return [...HELP_DEFAULT_OPEN_SECTIONS];
}

export default function HelpGuidesPage() {
  const { user } = useAuth();
  const [openSections, setOpenSections] = useState(getInitialOpenSections);
  const [activeSection, setActiveSection] = useState(() => getHashSection() || HELP_DEFAULT_OPEN_SECTIONS[0]);

  const openAndScroll = useCallback((sectionId) => {
    if (!sectionId) return;
    setOpenSections((prev) => (prev.includes(sectionId) ? prev : [...prev, sectionId]));
    setActiveSection(sectionId);
    const nextHash = `#${sectionId}`;
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, '', nextHash);
    }
    window.setTimeout(() => scrollToSection(sectionId), 80);
  }, []);

  useEffect(() => {
    const syncHash = () => {
      const hash = getHashSection();
      if (!hash) return;
      setOpenSections((prev) => (prev.includes(hash) ? prev : [...prev, hash]));
      setActiveSection(hash);
      window.setTimeout(() => scrollToSection(hash), 120);
    };

    syncHash();
    window.addEventListener('hashchange', syncHash);
    return () => window.removeEventListener('hashchange', syncHash);
  }, []);

  const backTo = user ? '/dashboard' : '/';
  const backLabel = user ? 'Dashboard' : 'Home';

  return (
    <div className="flex min-h-screen flex-col bg-[#f5efe6]" data-testid="help-guides-page">
      <header className="border-b border-[#e8e2d8]/90 bg-[#fffdf8]/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to={backTo}
              className="inline-flex shrink-0 items-center gap-1.5 text-sm text-[#6c6258] hover:text-[#2c2824]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">{backLabel}</span>
            </Link>
            <BrandWordmark variant="navApp" className="hidden text-lg sm:inline" />
          </div>
          <Link
            to="/faq"
            className="text-xs text-[#7a7268] hover:text-[#5c534a]"
          >
            FAQ
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-10 sm:px-8 sm:py-12">
        <header className="max-w-3xl">
          <p className="flex items-center gap-2 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-[#8b7355]">
            <LifeBuoy className="h-3.5 w-3.5" aria-hidden />
            MyLegacyBridge
          </p>
          <h1 className="mt-2 font-serif text-3xl font-light text-[#1c1917] sm:text-4xl">{HELP_PAGE.title}</h1>
          <p className="mt-3 text-base leading-relaxed text-[#5c534a] sm:text-lg">{HELP_PAGE.subtitle}</p>
          <p className="mt-2 text-sm leading-relaxed text-[#7a7268]">{HELP_PAGE.intro}</p>
          <p className="mt-4 text-xs text-[#8b7355]">
            Looking for feature overviews? See the{' '}
            <Link to="/guide" className="underline underline-offset-2 hover:text-[#5c4a32]">
              Feature Guide
            </Link>
            . For billing questions, see{' '}
            <Link to="/pricing" className="underline underline-offset-2 hover:text-[#5c4a32]">
              Pricing
            </Link>
            .
          </p>
        </header>

        <HelpQuickHelpRow cards={HELP_QUICK_CARDS} onOpenSection={openAndScroll} className="mt-8" />

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,14rem)_1fr] lg:items-start">
          <aside className="lg:sticky lg:top-6">
            <div className="rounded-2xl border border-[#e8e2d8]/80 bg-[#fffdf8]/90 p-4 shadow-sm">
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b7355]">Guide index</p>
              <HelpGuideNav items={HELP_NAV_ITEMS} activeId={activeSection} onItemClick={openAndScroll} />
            </div>
          </aside>

          <HelpGuideAccordion
            sections={HELP_GUIDE_SECTIONS}
            openSections={openSections}
            onOpenChange={(next) => {
              const opened = next.find((id) => !openSections.includes(id));
              setOpenSections(next);
              if (opened) {
                setActiveSection(opened);
                window.history.replaceState(null, '', `#${opened}`);
              }
            }}
          />
        </div>
      </main>

      <PublicFooter variant="full" />
    </div>
  );
}
